import { inject, Injectable } from '@angular/core'
import { AppInfo, ResourceJSON, ResourceQueryJSON } from '@monorepo/next-lab'
import { IlsModelService } from './ils-model.service'
import { BaseStorageService, MongoStorageService } from '@monorepo/angular-common'
import { BehaviorSubject } from 'rxjs'
import { MemoryStorageService } from './memory-storage.service'
import { TokenService } from '@monorepo/auth'

@Injectable({
  providedIn: 'root',
})
export class CachingStorageService extends BaseStorageService {
  private readonly ilsModelService = inject(IlsModelService)
  private readonly mongoStorageServer = inject(MongoStorageService)
  private readonly memoryStorageService = inject(MemoryStorageService)
  private readonly tokenService = inject(TokenService)

  private readonly cache = new Map<string, ResourceJSON>()
  private readonly cacheReady = new BehaviorSubject(false)
  private collaborationEnabled = false
  private realStorageServer: BaseStorageService

  constructor() {
    super()
    this.realStorageServer = this.mongoStorageServer // so that it is initialized
    this.updateRealStorageServer()
    this.debug = false
  }

  override getDebugLabel(): string {
    return `CachingStorageService`
  }

  override async initialize(): Promise<void> {
    this.logDebug('initialize')
    this.updateRealStorageServer()
    this.clear()
    this.providerId = this.ilsModelService.ilsId
    this.realStorageServer.providerId = this.ilsModelService.ilsId
    await this.realStorageServer.initialize()
    this.addConfigurationResourcesToCache()
    const loadResourcePromises: Promise<ResourceJSON[]>[] = []
    // if (this.collaborationEnabled) {
    //   loadResourcePromises.push(this.findCollaborationResources())
    // }
    loadResourcePromises.push(this.findUserResources())
    const resourcesLists = await Promise.all(loadResourcePromises)
    resourcesLists.forEach((resources) => resources.forEach((resource) => this.addResourceToCache(resource)))
    // await this.addUserResourcesToCache()
    this.cacheReady.next(true)
    this.logDebug('initialize done', this.cache.size, this.cache)
  }

  override clear() {
    super.clear()
    this.cache.clear()
    this.realStorageServer.clear()
  }

  override async createResourcePromise(resource: ResourceJSON): Promise<ResourceJSON> {
    this.logDebug('createResourcePromise', resource)

    const createdResource = await this.realStorageServer.createResourcePromise(resource)
    this.addResourceToCache(this.cloneResource(createdResource))
    return createdResource
  }

  override async deleteResourcePromise(resourceId: string): Promise<void> {
    this.logDebug('deleteResourcePromise', resourceId)

    await this.realStorageServer.deleteResourcePromise(resourceId)
    await this.waitForCacheReady()
    this.cache.delete(resourceId)
  }

  override async readResourcePromise(resourceId: string, fetchFromServer: boolean): Promise<ResourceJSON> {
    this.logDebug('readResourcePromise', resourceId, fetchFromServer)

    await this.waitForCacheReady()
    if (fetchFromServer || !this.hasResourceContentCached(resourceId)) {
      const resource = await this.realStorageServer.readResourcePromise(resourceId, fetchFromServer)
      this.addResourceToCache(this.cloneResource(resource))
      return resource
    } else {
      return this.cloneResource(this.cache.get(resourceId)!)
    }
  }

  override async updateResourcePromise(resource: ResourceJSON, async: boolean): Promise<ResourceJSON> {
    this.logDebug('updateResourcePromise', resource, async)

    const updatedResource = await this.realStorageServer.updateResourcePromise(resource, async)
    await this.waitForCacheReady()
    this.addResourceToCache(this.cloneResource(updatedResource))
    return updatedResource
  }

  override async resourceExistsPromise(resourceId: string, fetchFromServer: boolean): Promise<boolean> {
    this.logDebug('resourceExistsPromise', resourceId)

    await this.waitForCacheReady()
    if (fetchFromServer || !this.cache.has(resourceId)) {
      return await this.realStorageServer.resourceExistsPromise(resourceId, fetchFromServer)
    } else {
      return true
    }
  }

  override async queryResourcesPromise(query: ResourceQueryJSON, fetchFromServer: boolean): Promise<ResourceJSON[]> {
    this.logDebug('queryResourcesPromise', query, fetchFromServer)

    if (fetchFromServer) {
      return await this.realStorageServer.queryResourcesPromise(query, fetchFromServer)
    } else {
      await this.waitForCacheReady()
      return this.queryResourcesFromCachePromise(
        query,
        () => this.cache.keys(),
        (id) => this.cache.get(id)!,
      )
    }
  }

  private updateRealStorageServer(): void {
    if (this.ilsModelService.published) {
      this.realStorageServer = this.memoryStorageService
    } else {
      this.realStorageServer = this.mongoStorageServer
    }
  }

  private addConfigurationResourcesToCache(): void {
    this.logDebug('addConfigurationResourcesToCache')
    const addAppConfigurations: (apps: AppInfo[]) => void = (apps: AppInfo[]): void => {
      apps
        .filter((app) => app.configuration?.content)
        .forEach((app) => this.addConfigurationResourceToCache(app.configuration!))
    }
    const ilsStructure = this.ilsModelService.ilsStructure!
    addAppConfigurations(ilsStructure.apps)
    ilsStructure.phases.forEach((phase) => addAppConfigurations(phase.apps))
  }

  private addConfigurationResourceToCache(resource: ResourceJSON): void {
    if (this.collaborationEnabledInConfiguration(resource)) {
      this.collaborationEnabled = true
    }
    this.addResourceToCache(resource)
  }

  private collaborationEnabledInConfiguration(resource: ResourceJSON): boolean {
    return resource.content?.collaborationEnable ?? false
  }

  // private async findCollaborationResources(): Promise<ResourceJSON[]> {
  //   const collaborationGroupsQuery: ResourceQueryJSON = {
  //     resourceType: 'collaborationGroups',
  //     orderByModificationDate: true,
  //     maxResults: 1,
  //   }
  //   return this.realStorageServer.queryResourcesPromise(collaborationGroupsQuery, true)
  // }

  private async findUserResources(): Promise<ResourceJSON[]> {
    this.logDebug('addUserResourcesToCache')
    const userResourcesQuery: ResourceQueryJSON = {
      actorId: this.tokenService.getActor().id,
      returnMetadataOnly: false,
    }
    return this.realStorageServer.queryResourcesPromise(userResourcesQuery, true)
  }

  private async waitForCacheReady(): Promise<void> {
    return new Promise((resolve) => {
      if (this.cacheReady.value) {
        resolve()
      } else {
        const subscription = this.cacheReady.subscribe((cacheReady) => {
          if (cacheReady) {
            subscription.unsubscribe()
            resolve()
          }
        })
      }
    })
  }

  private addResourceToCache(resource: ResourceJSON): void {
    this.cache.set(resource.metadata.target.id, resource)
    // console.log('addResourceToCache', resource)
  }

  private hasResourceContentCached(resourceId: string): boolean {
    return this.cache.has(resourceId) && this.cache.get(resourceId)!.content
  }
}
